import React, { useState, useEffect, useRef } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

// HOOKS
import { useSocket } from 'hooks/useSocket';

// CONTEXT
import { useSeasonTheme } from 'context/SeasonThemeContext';
import { useTileStyle } from 'context/TileStyleContext';

// SERVICES
import { markAsRead as markNotificationAsRead } from 'services/notificaitonService';
import { insertTileStat } from 'services/statService';

// UTILS
import iconMap from 'utils/iconMap';

// ASSETS
import batSvg from 'assets/images/themes/halloween/bat.svg';

const NotificationTile = ({ user_id, tile_id, title, url, notif, isPrivate, onPrivateClick, isEditing = false, isViewing = false }) => {
    const [displayedNotifications, setDisplayedNotifications] = useState([]);
    const [readNotifications, setReadNotifications] = useState(new Set());
    const [isReadyToAnimate, setIsReadyToAnimate] = useState(false);
    const containerRef = useRef(null);
    const { socket } = useSocket();
    const { seasonTheme } = useSeasonTheme();
    const { tileStyle } = useTileStyle();
    const isLiquidGlass = tileStyle === 'liquid-glass';
    const [, setTime] = useState(Date.now());

    const EmptyInBoxIcon = iconMap['emptyInBox'];

    // Nettoie le préfixe "[ANNONCE] " ou "[ANNONCE] - " ou "[ANNONCE] – " au début du contenu
    const cleanContent = (raw) => {
        if (typeof raw !== 'string') return raw;
        return raw.replace(/^\[ANNONCE\]\s*[-–]?\s*/i, '');
    };

    useEffect(() => {
        if (notif) {
            try {
                const parsedNotif = JSON.parse(notif);
                const transformedNotif = parsedNotif.map(n => ({
                    id: n.NTF_ID,
                    content: cleanContent(n.NTF_CONTENT),
                    url: n.NTF_URL,
                    createdAt: n.NTF_DATE,
                    senderName: n.NTF_AUTHOR,
                    isRead: n.IS_READ
                }));
                
                transformedNotif.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

                setDisplayedNotifications(transformedNotif);

                const readNotifSet = new Set(transformedNotif.filter(n => n.isRead).map(n => n.id));
                setReadNotifications(readNotifSet);
                
                // Réinitialiser l'état d'animation pour permettre le recalcul de la hauteur
                setIsReadyToAnimate(false);
            } catch (error) {
                console.error('Failed to parse notif:', error);
            }
        }
    }, [notif]);

    // Calculer la hauteur avant d'animer : attendre que le DOM soit rendu puis activer l'animation
    useEffect(() => {
        if (displayedNotifications.length === 0) {
            setIsReadyToAnimate(false);
            return;
        }

        // Attendre que le navigateur calcule les dimensions (plusieurs frames + petit délai)
        const rafId1 = requestAnimationFrame(() => {
            const rafId2 = requestAnimationFrame(() => {
                const rafId3 = requestAnimationFrame(() => {
                    // Petit délai supplémentaire pour être sûr que la scrollbar est calculée
                    setTimeout(() => {
                        setIsReadyToAnimate(true);
                    }, 50);
                });
                return () => cancelAnimationFrame(rafId3);
            });
            return () => cancelAnimationFrame(rafId2);
        });

        return () => {
            cancelAnimationFrame(rafId1);
        };
    }, [displayedNotifications.length]);

    useEffect(() => {
        if (!socket) {
            console.error("Socket n'est pas défini");
            return;
        }
    
        socket.on('data-received', (newNotification) => {
            if (!newNotification.notificationId) {
                console.error("Notification reçue sans ID:", newNotification);
                return;
            }
    
            const formattedNotification = {
                id: newNotification.notificationId,
                content: cleanContent(newNotification.content),
                url: newNotification.url,
                createdAt: newNotification.createdAt,
                senderName: newNotification.senderName,
                isRead: false
            };
    
            setDisplayedNotifications(prev => {
                const updated = [formattedNotification, ...prev];
                // Réinitialiser l'animation pour recalculer la hauteur avec la nouvelle notification
                setIsReadyToAnimate(false);
                return updated;
            });
        });
    
        return () => {
            socket.off('data-received');
        };
    }, [socket]);    

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                setTime(Date.now());
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    const formatNotificationDate = (date) => {
        const now = new Date();
        const diffInMinutes = Math.floor((now - date) / 60000);

        if (diffInMinutes < 1) {
            return "il y a quelques secondes";
        } else if (diffInMinutes < 60) {
            return `il y a ${diffInMinutes} min`;
        } else if (diffInMinutes < 1440) {
            const hours = Math.floor(diffInMinutes / 60);
            return `il y a ${hours} h`;
        } else if (diffInMinutes < 10080) {
            return format(date, "EEEE HH:mm", { locale: fr });
        } else if (diffInMinutes < 43200) {
            return format(date, "EEE dd MMM", { locale: fr });
        } else {
            return format(date, "dd MMM yyyy", { locale: fr });
        }
    };

    const markAsRead = async (notificationId) => {
        if (!readNotifications.has(notificationId)) {
            try {
                await markNotificationAsRead(user_id, notificationId);
                setReadNotifications(prev => new Set([...prev, notificationId]));
            } catch (error) {
                console.error('Erreur lors de la mise à jour de la notification:', error);
            }
        }
    };

    const markAllAsRead = async () => {
        try {
            const unreadNotifs = displayedNotifications.filter(n => !readNotifications.has(n.id));
            await Promise.all(unreadNotifs.map(n => markNotificationAsRead(user_id, n.id)));
            setReadNotifications(new Set(displayedNotifications.map(n => n.id)));
        } catch (error) {
            console.error('Erreur lors de la mise à jour des notifications:', error);
        }
    };

    return (
        <div className="w-full h-full p-4 rounded-lg overflow-hidden flex flex-col relative">
            {/* Chauves-souris pour Halloween */}
            {seasonTheme === 'halloween' && (
                <>
                    <div className="absolute top-1 left-2 size-12">
                        <img src={batSvg} alt="Chauve-souris" className="w-full h-full" />
                    </div>
                </>
            )}
            
            {!isEditing && !isViewing ? (
                <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-lg md:text-xl lg:text-lg poppins-bold text-center text-blanc"
                    onClick={() => {
                        insertTileStat(user_id, tile_id);
                    }}
                >
                    {title}
                </a>
            ) : (
                <p className="text-lg md:text-xl lg:text-lg poppins-bold text-center text-blanc">
                    {title}
                </p>
            )}

            {displayedNotifications.some(n => !readNotifications.has(n.id)) && !isViewing && (
                <button
                    className={`liquid-glass-element my-2 mr-2 px-4 py-1.5 rounded-md self-end text-xs font-medium border border-blanc-70 text-blanc transition-all duration-200 shadow-sm hover:shadow-md ${isLiquidGlass ? '' : 'hover:bg-blanc hover:text-noir'}`}
                    onClick={markAllAsRead}
                >
                    Tout marquer comme lu
                </button>
            )}

            <div ref={containerRef} className="px-2 mt-2 rounded-lg flex-grow relative overflow-y-auto hide-horizontal-scrollbar">
                {displayedNotifications.length > 0 ? (
                    displayedNotifications.map((notification, index) => (
                        <>
                            {!isEditing && !isViewing ? (
                                <a 
                                    key={notification.id}
                                    href={notification.url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="group relative block"
                                    onClick={() => {
                                        markAsRead(notification.id);
                                        insertTileStat(user_id, tile_id);
                                    }}
                                >
                                    <div
                                        className={
                                            `liquid-glass-element ${!readNotifications.has(notification.id) ? 'liquid-glass-element--unread' : ''} ${index === 0 ? '' : 'mt-3'} p-3.5 max-h-28 rounded-lg relative
                                            bg-blanc-70 dark:bg-noir-70
                                            ${isReadyToAnimate ? 'animate-notification-slide-in' : ''}
                                            transition-all duration-300 ease-out
                                            ${!readNotifications.has(notification.id)
                                                ? `${isLiquidGlass ? 'text-blanc' : 'border-l-4 border-framboise-variant-1 text-noir dark:text-blanc'} shadow-sm hover:shadow-md hover:scale-[1.02]`
                                                : `border border-blanc-30 dark:border-noir-30 ${isLiquidGlass ? 'text-blanc/80' : 'text-noir-70 dark:text-blanc-70'} hover:border-blanc-50 dark:hover:border-noir-50 hover:bg-blanc-80 dark:hover:bg-noir-60`}`
                                        }
                                        style={isReadyToAnimate 
                                            ? { animationDelay: `${index * 0.15}s` }
                                            : { opacity: 0, transform: 'translateX(100%) scale(0.9)' }
                                        }
                                    >
                                        <div className="flex items-start gap-2">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex justify-between items-start mb-1.5 gap-2">
                                                    <h3 className={`text-xs leading-tight ${!readNotifications.has(notification.id) ? `poppins-extrabold ${isLiquidGlass ? 'text-blanc' : 'text-noir dark:text-blanc'}` : 'poppins-semibold'}`}>
                                                        {notification.senderName}
                                                    </h3>
                                                    <span className={`text-xs flex-shrink-0 ${!readNotifications.has(notification.id) ? (isLiquidGlass ? 'text-blanc/70' : 'text-noir-60 dark:text-blanc-60') : (isLiquidGlass ? 'text-blanc/70' : 'text-noir-70 dark:text-blanc-70')}`}>
                                                        {formatNotificationDate(new Date(notification.createdAt))}
                                                    </span>
                                                </div>
                                                <p className={`text-xs leading-relaxed line-clamp-3 overflow-hidden text-ellipsis break-words ${!readNotifications.has(notification.id) ? `poppins-medium ${isLiquidGlass ? 'text-blanc/90' : 'text-noir-90 dark:text-blanc-90'}` : 'poppins-regular'}`}>
                                                    {notification.content}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </a>
                            ) : (
                                <div key={index} className={`liquid-glass-element mb-4 p-3.5 bg-blanc-70 dark:bg-noir-70 rounded-lg ${isLiquidGlass ? 'text-blanc/80' : 'text-noir dark:text-blanc'} max-h-28 border border-blanc-30 dark:border-noir-30 ${isReadyToAnimate ? 'animate-notification-slide-in' : ''}`} style={isReadyToAnimate ? { animationDelay: `${index * 0.15}s` } : { opacity: 0, transform: 'translateX(100%) scale(0.9)' }}>
                                    <div className="flex justify-between items-start mb-1.5 gap-2">
                                        <h3 className="text-xs poppins-semibold leading-tight">{notification.senderName}</h3>
                                        <span className={`text-xs flex-shrink-0 ${isLiquidGlass ? 'text-blanc/70' : 'text-noir-70 dark:text-blanc-70'}`}>
                                            {formatNotificationDate(new Date(notification.createdAt))}
                                        </span>
                                    </div>
                                    <p className="text-xs line-clamp-3 leading-relaxed overflow-hidden text-ellipsis break-words poppins-regular">
                                        {notification.content}
                                    </p>
                                </div>
                            )}
                        </>
                    ))
                ) : (
                    <div className="flex flex-col justify-center items-center h-full">
                        <EmptyInBoxIcon className="size-10 md:size-14 lg:size-20 fill-current" />
                        <p className="text-sm md:text-lg lg:text-xl mt-6 text-center dark:text-blanc">Aucune notification</p>
                    </div>
                )}
            </div>
        </div>
    );
  };
  
  export default NotificationTile;